import { useEffect, useMemo, useRef, useState } from 'react';
import {
  makeStyles, tokens, Title1, Title3, Text, Button, Spinner,
  Badge, Checkbox, Dropdown, Option, Field, Divider,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
} from '@fluentui/react-components';
import { listDatasets } from '../api/datasets';
import { listPrompts } from '../api/prompts';
import { runMatrix, preflightMatrix } from '../api/matrix';
import type { MatrixRunResponse, MatrixAggregateDto, MatrixCellDto, MatrixPreflightResponse } from '../api/matrix';
import type { GoldenDatasetSummary, PromptSummary, SignalCareRole } from '../api/types';
import type { ExecSeriesPoint } from '../components/execTypes';
import { ExecutiveRecommendation } from '../components/ExecutiveRecommendation';
import { ScoreBarChart } from '../components/ScoreBarChart';
import { CostQualityScatter } from '../components/CostQualityScatter';
import { useChatModels } from '../hooks/useChatModels';
import { providerBadgeColor, providerLabel } from '../lib/chatModelId';

// ============================================================================
// Constants
// ============================================================================

const ROLE_LABEL: Record<SignalCareRole, string> = {
  MemberIntake: 'Member Intake',
  CareCoordinator: 'Care Coordinator',
  Lpn: 'LPN',
  Rn: 'RN',
  Doctor: 'Doctor',
  Billing: 'Billing',
  Claims: 'Claims',
};

const LOADING_MESSAGES = [
  'Fanning out to providers...',
  'Scoring answers against expected keywords...',
  'Ranking prompt × provider cells...',
  'Building recommendation...',
];

function scoreColor(v: number): 'success' | 'warning' | 'danger' {
  if (v >= 0.75) return 'success';
  if (v >= 0.5)  return 'warning';
  return 'danger';
}

function pct(v: number) {
  return `${(v * 100).toFixed(0)}%`;
}

// ============================================================================
// Styles
// ============================================================================

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '16px' },

  section: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '20px 24px',
  },

  errorBanner: {
    background: tokens.colorPaletteRedBackground1,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
    borderRadius: '8px',
    padding: '12px 16px',
    color: tokens.colorPaletteRedForeground1,
  },

  runControls: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    marginTop: '16px',
    flexWrap: 'wrap',
  },

  promptList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginTop: '10px',
    maxHeight: '240px',
    overflow: 'auto',
  },

  promptRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 8px',
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground2,
  },

  providerRow: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    marginTop: '10px',
  },

  providerCheck: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '6px',
    background: tokens.colorNeutralBackground2,
  },

  modelGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '10px 12px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '6px',
    background: tokens.colorNeutralBackground2,
    minWidth: '240px',
  },
  modelGroupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  },
  modelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    paddingLeft: '4px',
  },

  preflightBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    borderRadius: '6px',
    background: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    marginTop: '8px',
    fontSize: '13px',
  },
  preflightWarn: {
    background: tokens.colorPaletteYellowBackground1,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
  },

  // Aggregate grid table
  gridTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
    marginTop: '12px',
  },
  th: {
    textAlign: 'center',
    padding: '8px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground2,
    fontWeight: '600',
    background: tokens.colorNeutralBackground2,
  },
  thLeft: {
    textAlign: 'left',
    padding: '8px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground2,
    fontWeight: '600',
    background: tokens.colorNeutralBackground2,
  },
  td: {
    padding: '10px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    verticalAlign: 'middle',
  },
  tdCenter: {
    padding: '10px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    textAlign: 'center',
    verticalAlign: 'middle',
  },

  aggCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
  },

  winnerCell: {
    background: tokens.colorBrandBackground2,
    borderRadius: '6px',
  },

  // Results header row
  resultsHeader: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    alignItems: 'baseline',
    marginBottom: '12px',
  },

  // Dialog drill-down
  drillTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
    marginTop: '8px',
  },
  drillTh: {
    textAlign: 'left',
    padding: '6px 10px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground2,
    fontWeight: '600',
    background: tokens.colorNeutralBackground2,
  },
  drillTd: {
    padding: '8px 10px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    verticalAlign: 'top',
  },
  answerSnippet: {
    maxWidth: '320px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
  },
});

// ============================================================================
// MatrixLab page
// ============================================================================

export default function MatrixLab() {
  const s = useStyles();

  // Data
  const [datasets, setDatasets] = useState<GoldenDatasetSummary[]>([]);
  const [prompts, setPrompts] = useState<PromptSummary[]>([]);

  // Selection
  const [selectedDatasetId, setSelectedDatasetId] = useState<string | null>(null);
  const [selectedPromptIds, setSelectedPromptIds] = useState<Set<string>>(new Set());
  const [selectedChatModelIds, setSelectedChatModelIds] = useState<Set<string>>(new Set());

  // Model catalog (configured only) — grouped by provider for the picker.
  const { models, byProvider, byChatModelId } = useChatModels(true);

  // On first load of the catalog, pre-select every configured model (mirrors
  // the pre-M4 default of "run every configured provider").
  const catalogSeededRef = useRef(false);
  useEffect(() => {
    if (models.length > 0 && !catalogSeededRef.current) {
      setSelectedChatModelIds(new Set(models.map(m => m.chatModelId)));
      catalogSeededRef.current = true;
    }
  }, [models]);

  // Preflight estimate
  const [preflight, setPreflight] = useState<MatrixPreflightResponse | null>(null);
  const [preflightErr, setPreflightErr] = useState<string | null>(null);

  // Run state
  const [busy, setBusy] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState(LOADING_MESSAGES[0]);
  const loadingMsgRef = useRef(0);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<MatrixRunResponse | null>(null);

  // Drill-down dialog
  const [drillCell, setDrillCell] = useState<MatrixAggregateDto | null>(null);

  // Load datasets once
  useEffect(() => {
    (async () => {
      try {
        const list = await listDatasets();
        setDatasets(list);
      } catch (e) {
        setErr(`Could not load datasets: ${(e as Error).message}`);
      }
    })();
  }, []);

  // Derive selected dataset
  const selectedDataset = useMemo(
    () => datasets.find(d => d.id === selectedDatasetId) ?? null,
    [datasets, selectedDatasetId],
  );

  // Load prompts when dataset changes (filter by role)
  useEffect(() => {
    if (!selectedDataset) {
      setPrompts([]);
      setSelectedPromptIds(new Set());
      return;
    }
    (async () => {
      try {
        const list = await listPrompts(selectedDataset.role as SignalCareRole);
        setPrompts(list);
        setSelectedPromptIds(new Set()); // reset prompt selection on dataset change
      } catch (e) {
        setErr(`Could not load prompts: ${(e as Error).message}`);
      }
    })();
  }, [selectedDataset]);

  // Loading message rotation
  useEffect(() => {
    if (!busy) return;
    loadingMsgRef.current = 0;
    setLoadingMsg(LOADING_MESSAGES[0]);
    const id = setInterval(() => {
      loadingMsgRef.current = (loadingMsgRef.current + 1) % LOADING_MESSAGES.length;
      setLoadingMsg(LOADING_MESSAGES[loadingMsgRef.current]);
    }, 3000);
    return () => clearInterval(id);
  }, [busy]);

  function togglePrompt(id: string) {
    setSelectedPromptIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleChatModel(id: string) {
    setSelectedChatModelIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleProviderGroup(providerId: string) {
    const groupIds = (byProvider.get(providerId) ?? []).map(m => m.chatModelId);
    const allOn = groupIds.every(id => selectedChatModelIds.has(id));
    setSelectedChatModelIds(prev => {
      const next = new Set(prev);
      for (const id of groupIds) {
        if (allOn) next.delete(id); else next.add(id);
      }
      return next;
    });
  }

  const canRun =
    !busy &&
    selectedDatasetId !== null &&
    selectedPromptIds.size > 0 &&
    selectedChatModelIds.size > 0;

  // Preflight: debounced on selection change. Guards against dataset/model
  // unset before firing. Silently fails — banner just doesn't render.
  useEffect(() => {
    if (!selectedDatasetId || selectedPromptIds.size === 0 || selectedChatModelIds.size === 0) {
      setPreflight(null);
      setPreflightErr(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await preflightMatrix({
          datasetId: selectedDatasetId,
          promptIds: Array.from(selectedPromptIds),
          chatModelIds: Array.from(selectedChatModelIds),
        });
        if (!cancelled) { setPreflight(res); setPreflightErr(null); }
      } catch (e) {
        if (!cancelled) { setPreflight(null); setPreflightErr((e as Error).message); }
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [selectedDatasetId, selectedPromptIds, selectedChatModelIds]);

  const preflightWarn =
    preflight !== null &&
    (preflight.estimatedCostUsd > 1.0 || preflight.estimatedSeconds > 300);

  async function handleRun() {
    if (!selectedDatasetId) return;
    setErr(null);
    setBusy(true);
    setResult(null);
    try {
      const res = await runMatrix({
        datasetId: selectedDatasetId,
        promptIds: Array.from(selectedPromptIds),
        chatModelIds: Array.from(selectedChatModelIds),
      });
      setResult(res);
    } catch (e) {
      const msg = (e as Error).message;
      // Surface server error text (API client prefixes "API NNN: <body>")
      const match = msg.match(/^API \d+: (.+)$/s);
      setErr(match ? match[1] : msg);
    } finally {
      setBusy(false);
    }
  }

  // Result pivoting is now model-aware. RankedCells is "{promptId}|{chatModelId}"
  // since M2 — matches aggregate.chatModelId directly.

  const topCell = useMemo<MatrixAggregateDto | null>(() => {
    if (!result || result.aggregates.length === 0) return null;
    const [topKey] = result.rankedCells;
    if (!topKey) return null;
    const [promptId, chatModelId] = topKey.split('|');
    return result.aggregates.find(a => a.promptId === promptId && a.chatModelId === chatModelId) ?? null;
  }, [result]);

  const sortedAggregates = useMemo<MatrixAggregateDto[]>(() => {
    if (!result) return [];
    const rankMap = new Map(result.rankedCells.map((k, i) => [k, i]));
    return [...result.aggregates].sort((a, b) => {
      const ka = `${a.promptId}|${a.chatModelId}`;
      const kb = `${b.promptId}|${b.chatModelId}`;
      return (rankMap.get(ka) ?? 999) - (rankMap.get(kb) ?? 999);
    });
  }, [result]);

  const resultPromptIds = useMemo<string[]>(() => {
    if (!result) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const key of result.rankedCells) {
      const [pid] = key.split('|');
      if (!seen.has(pid)) { seen.add(pid); out.push(pid); }
    }
    for (const agg of result.aggregates) {
      if (!seen.has(agg.promptId)) { seen.add(agg.promptId); out.push(agg.promptId); }
    }
    return out;
  }, [result]);

  // Chat-model columns for the grid, in rank order first.
  const resultChatModelIds = useMemo<string[]>(() => {
    if (!result) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const key of result.rankedCells) {
      const [, cmid] = key.split('|');
      if (!seen.has(cmid)) { seen.add(cmid); out.push(cmid); }
    }
    for (const agg of result.aggregates) {
      if (!seen.has(agg.chatModelId)) { seen.add(agg.chatModelId); out.push(agg.chatModelId); }
    }
    return out;
  }, [result]);

  // Aggregate lookup keyed by "promptId|chatModelId" — matches RankedCells.
  const aggByKey = useMemo<Map<string, MatrixAggregateDto>>(() => {
    const m = new Map<string, MatrixAggregateDto>();
    for (const agg of (result?.aggregates ?? [])) {
      m.set(`${agg.promptId}|${agg.chatModelId}`, agg);
    }
    return m;
  }, [result]);

  const promptNameById = useMemo<Map<string, string>>(() => {
    const m = new Map<string, string>();
    for (const agg of (result?.aggregates ?? [])) {
      m.set(agg.promptId, agg.promptName);
    }
    return m;
  }, [result]);

  // ChatModel display info from aggregates + catalog fallback.
  const chatModelInfoById = useMemo<Map<string, { label: string; providerId: string; providerName: string; modelName: string }>>(() => {
    const m = new Map<string, { label: string; providerId: string; providerName: string; modelName: string }>();
    for (const agg of (result?.aggregates ?? [])) {
      m.set(agg.chatModelId, {
        label: `${agg.providerName} · ${agg.modelName}`,
        providerId: agg.providerId,
        providerName: agg.providerName,
        modelName: agg.modelName,
      });
    }
    for (const [id, d] of byChatModelId) {
      if (!m.has(id)) m.set(id, {
        label: d.displayLabel,
        providerId: d.providerId,
        providerName: d.providerName,
        modelName: d.modelName,
      });
    }
    return m;
  }, [result, byChatModelId]);

  // ExecSeriesPoint[] — pivots on chatModelId now so charts group per model.
  const points = useMemo<ExecSeriesPoint[]>(() =>
    result?.aggregates.map(a => ({
      key: `${a.promptId}|${a.chatModelId}`,
      label: `${a.promptName} × ${a.providerName} · ${a.modelName}`,
      overallScore: a.avgOverallScore,
      costUsd: a.totalCostUsd,
      latencyMs: a.avgLatencyMs,
      hallucinationRisk: a.avgHallucinationRisk,
      isError: a.errorCount > 0 && a.errorCount === a.runCount,
      errorText: a.errorCount > 0 ? `${a.errorCount}/${a.runCount} errored` : undefined,
    })) ?? [],
  [result]);

  // Rank map for cell badges
  const rankByKey = useMemo<Map<string, number>>(() => {
    const m = new Map<string, number>();
    (result?.rankedCells ?? []).forEach((k, i) => m.set(k, i + 1));
    return m;
  }, [result]);

  // Drill-down: cells for the selected aggregate
  const drillCells = useMemo<MatrixCellDto[]>(() => {
    if (!drillCell || !result) return [];
    return result.cells.filter(
      c => c.promptId === drillCell.promptId && c.chatModelId === drillCell.chatModelId,
    ).sort((a, b) => a.questionOrderIndex - b.questionOrderIndex);
  }, [drillCell, result]);

  const totalCells = result
    ? result.questionCount * result.promptCount * result.chatModelCount
    : 0;

  return (
    <div className={s.page}>
      {/* ---- Page header ---- */}
      <div>
        <Title1 as="h1">Matrix Benchmark — Prompt × Provider</Title1>
        <Text size={200} block style={{ color: tokens.colorNeutralForeground2 }}>
          Run every question in a dataset through multiple prompts and providers simultaneously.
          The result is a ranked grid showing which prompt + provider combination performs best
          for your care-team role.
        </Text>
      </div>

      {/* ---- Error banner ---- */}
      {err && (
        <div className={s.errorBanner}>
          <Text weight="semibold" block>Error</Text>
          <Text size={300}>{err}</Text>
        </div>
      )}

      {/* ---- 1) Dataset picker ---- */}
      <div className={s.section}>
        <Title3 as="h2">Dataset</Title3>
        <Text size={200} block style={{ color: tokens.colorNeutralForeground2, marginTop: 4 }}>
          Pick a golden dataset. The role is locked to the dataset — only prompts with the
          same role will appear in step 2.
        </Text>
        <div style={{ marginTop: 12, maxWidth: 480 }}>
          <Field label="Golden dataset">
            <Dropdown
              placeholder="Select a dataset..."
              value={selectedDataset?.name ?? ''}
              selectedOptions={selectedDatasetId ? [selectedDatasetId] : []}
              onOptionSelect={(_e, d) => {
                if (d.optionValue) {
                  setSelectedDatasetId(d.optionValue);
                  setResult(null);
                  setErr(null);
                }
              }}
              disabled={busy}
            >
              {datasets.map(ds => (
                <Option key={ds.id} value={ds.id} text={ds.name}>
                  {ds.name}
                  <Badge appearance="outline" size="small" style={{ marginLeft: 8 }}>
                    {ROLE_LABEL[ds.role as SignalCareRole] ?? ds.role}
                  </Badge>
                  <Badge appearance="outline" size="small" style={{ marginLeft: 4 }}>
                    {ds.questionCount} Q
                  </Badge>
                </Option>
              ))}
            </Dropdown>
          </Field>
          {selectedDataset && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
              <Badge appearance="tint" color="brand">
                Role: {ROLE_LABEL[selectedDataset.role as SignalCareRole] ?? selectedDataset.role}
              </Badge>
              <Badge appearance="tint">
                {selectedDataset.questionCount} questions
              </Badge>
            </div>
          )}
        </div>
      </div>

      {/* ---- 2) Prompt picker ---- */}
      <div className={s.section}>
        <Title3 as="h2">Prompts</Title3>
        <Text size={200} block style={{ color: tokens.colorNeutralForeground2, marginTop: 4 }}>
          Select one or more prompts to test. Only prompts with the same role as the dataset
          are shown (backend rejects mismatched roles with 400).
        </Text>
        {!selectedDataset && (
          <Text size={300} block style={{ color: tokens.colorNeutralForeground3, marginTop: 12 }}>
            Pick a dataset first.
          </Text>
        )}
        {selectedDataset && prompts.length === 0 && (
          <Text size={300} block style={{ color: tokens.colorNeutralForeground3, marginTop: 12 }}>
            No prompts found for role{' '}
            <strong>{ROLE_LABEL[selectedDataset.role as SignalCareRole] ?? selectedDataset.role}</strong>.{' '}
            Create one in <strong>Prompt Optimization</strong>.
          </Text>
        )}
        {prompts.length > 0 && (
          <div className={s.promptList}>
            {prompts.map(p => (
              <div key={p.id} className={s.promptRow}>
                <Checkbox
                  checked={selectedPromptIds.has(p.id)}
                  onChange={() => togglePrompt(p.id)}
                  disabled={busy}
                  label={
                    <span>
                      <Text weight="semibold">{p.name}</Text>
                      {p.productionVersionNumber !== null && (
                        <Badge appearance="tint" color="success" size="small" style={{ marginLeft: 6 }}>
                          v{p.productionVersionNumber}
                        </Badge>
                      )}
                    </span>
                  }
                />
                <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginLeft: 'auto' }}>
                  {p.status}
                </Text>
              </div>
            ))}
          </div>
        )}
        {selectedPromptIds.size > 0 && (
          <Text size={200} block style={{ color: tokens.colorNeutralForeground2, marginTop: 8 }}>
            {selectedPromptIds.size} prompt{selectedPromptIds.size !== 1 ? 's' : ''} selected
          </Text>
        )}
      </div>

      {/* ---- 3) Model picker ---- M4: grouped by provider header. */}
      <div className={s.section}>
        <Title3 as="h2">Models</Title3>
        <Text size={200} block style={{ color: tokens.colorNeutralForeground2, marginTop: 4 }}>
          Choose which chat models to include. Grouped by provider — click a provider
          header to toggle all models in that group. Unconfigured models don't appear.
        </Text>
        <div className={s.providerRow}>
          {Array.from(byProvider.entries()).map(([providerId, group]) => {
            const groupIds = group.map(m => m.chatModelId);
            const allOn = groupIds.every(id => selectedChatModelIds.has(id));
            const someOn = groupIds.some(id => selectedChatModelIds.has(id));
            return (
              <div key={providerId} className={s.modelGroup}>
                <div className={s.modelGroupHeader}>
                  <Checkbox
                    checked={allOn ? true : someOn ? 'mixed' : false}
                    onChange={() => toggleProviderGroup(providerId)}
                    disabled={busy}
                    label={
                      <Badge color={providerBadgeColor(providerId)} appearance="tint">
                        {providerLabel(providerId)}
                      </Badge>
                    }
                  />
                  <Text size={100} style={{ color: tokens.colorNeutralForeground3, marginLeft: 'auto' }}>
                    {group.filter(m => selectedChatModelIds.has(m.chatModelId)).length}/{group.length}
                  </Text>
                </div>
                {group.map(m => (
                  <div key={m.chatModelId} className={s.modelRow}>
                    <Checkbox
                      checked={selectedChatModelIds.has(m.chatModelId)}
                      onChange={() => toggleChatModel(m.chatModelId)}
                      disabled={busy}
                      label={
                        <span>
                          <Text size={200}>{m.modelName}</Text>
                          {m.tierHint && (
                            <Badge appearance="outline" size="small" style={{ marginLeft: 6 }}>
                              {m.tierHint}
                            </Badge>
                          )}
                        </span>
                      }
                    />
                  </div>
                ))}
              </div>
            );
          })}
          {models.length === 0 && (
            <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
              No configured chat models found. Set provider API keys in <code>.env</code>.
            </Text>
          )}
        </div>

        {/* ---- Preflight banner ---- */}
        {preflight && (
          <div className={`${s.preflightBanner} ${preflightWarn ? s.preflightWarn : ''}`}>
            <Text size={200} weight="semibold">
              Preflight:
            </Text>
            <Text size={200}>
              {preflight.cellCount} cells · est. ${preflight.estimatedCostUsd.toFixed(4)} ·
              ~{preflight.estimatedSeconds.toFixed(0)}s
            </Text>
            {preflightWarn && (
              <Badge color="warning" appearance="tint" size="small">
                Above threshold (&gt;$1.00 or &gt;5 min)
              </Badge>
            )}
          </div>
        )}
        {preflightErr && (
          <div className={s.preflightBanner}>
            <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>
              Preflight failed: {preflightErr}
            </Text>
          </div>
        )}

        {/* ---- Run button ---- */}
        <div className={s.runControls}>
          <Button
            appearance="primary"
            size="large"
            onClick={handleRun}
            disabled={!canRun}
          >
            {busy ? 'Running matrix...' : (
              canRun
                ? `Run Matrix (${selectedDataset?.questionCount ?? 0}Q × ${selectedPromptIds.size}P × ${selectedChatModelIds.size}M)`
                : 'Run Matrix'
            )}
          </Button>
          {busy && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Spinner size="tiny" />
              <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>{loadingMsg}</Text>
            </div>
          )}
        </div>
      </div>

      {/* ============================================================
          Results section
          ============================================================ */}
      {result && (
        <>
          {/* ---- Results header ---- */}
          <div className={s.section}>
            <div className={s.resultsHeader}>
              <Title3 as="h2" style={{ margin: 0 }}>Results — {result.datasetName}</Title3>
              <Badge appearance="tint" color="brand">
                {ROLE_LABEL[result.role as SignalCareRole] ?? result.role}
              </Badge>
              <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
                {result.questionCount}Q × {result.promptCount}P × {result.chatModelCount}M = {totalCells} cells
              </Text>
            </div>
            {topCell && (
              <Text size={300} block style={{ color: tokens.colorNeutralForeground2 }}>
                Top combination:{' '}
                <strong>{topCell.promptName}</strong> × <strong>{topCell.providerName} · {topCell.modelName}</strong>
                {' '}·{' '}
                <Badge color={scoreColor(topCell.avgOverallScore)} appearance="tint">
                  {pct(topCell.avgOverallScore)} overall
                </Badge>
              </Text>
            )}
          </div>

          {/* ---- Recommendation ---- */}
          <ExecutiveRecommendation
            points={points}
            serverRecommendation={result.recommendation}
            entityLabel="cells"
          />

          {/* ---- Charts: score bar + cost-quality scatter ---- */}
          <div
            className={s.section}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
              gap: '16px',
              padding: '20px 24px',
            }}
          >
            <ScoreBarChart points={points} title="Overall score by prompt × provider" />
            <CostQualityScatter points={points} title="Cost vs quality" qualityFloor={0.6} />
          </div>

          {/* ---- Aggregate grid: Prompt × ChatModel ---- */}
          <div className={s.section}>
            <Title3 as="h2">Aggregate Grid — Prompt × Model</Title3>
            <Text size={200} block style={{ color: tokens.colorNeutralForeground2, marginTop: 4 }}>
              Each cell shows the average overall score across all questions, plus cost and
              latency. Click a cell to see per-question detail. Rank badges mark the top
              combinations.
            </Text>
            <div style={{ overflowX: 'auto', marginTop: 12 }}>
              <table className={s.gridTable}>
                <thead>
                  <tr>
                    <th className={s.thLeft}>Prompt</th>
                    {resultChatModelIds.map(cmid => {
                      const info = chatModelInfoById.get(cmid);
                      return (
                        <th key={cmid} className={s.th}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <Badge color={providerBadgeColor(info?.providerId ?? '')} appearance="tint">
                              {info?.providerName ?? cmid}
                            </Badge>
                            <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>
                              {info?.modelName ?? ''}
                            </Text>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {resultPromptIds.map(pid => (
                    <tr key={pid}>
                      <td className={s.td}>
                        <Text weight="semibold" size={300}>
                          {promptNameById.get(pid) ?? pid}
                        </Text>
                      </td>
                      {resultChatModelIds.map(cmid => {
                        const key = `${pid}|${cmid}`;
                        const agg = aggByKey.get(key);
                        const rank = rankByKey.get(key);
                        const isWinner = rank === 1;
                        return (
                          <td
                            key={cmid}
                            className={s.tdCenter}
                            style={{ cursor: agg ? 'pointer' : 'default' }}
                            onClick={() => agg && setDrillCell(agg)}
                          >
                            {agg ? (
                              <div
                                className={s.aggCell}
                                style={isWinner ? { padding: '6px 8px' } : { padding: '6px 8px' }}
                              >
                                {rank && rank <= 3 && (
                                  <Badge
                                    appearance="filled"
                                    color={rank === 1 ? 'success' : rank === 2 ? 'warning' : 'informative'}
                                    size="small"
                                  >
                                    #{rank}
                                  </Badge>
                                )}
                                {agg.errorCount > 0 && agg.errorCount === agg.runCount ? (
                                  <Badge color="danger" appearance="tint">
                                    {agg.errorCount} errors
                                  </Badge>
                                ) : (
                                  <>
                                    <Badge
                                      color={scoreColor(agg.avgOverallScore)}
                                      appearance={isWinner ? 'filled' : 'tint'}
                                    >
                                      {pct(agg.avgOverallScore)}
                                    </Badge>
                                    <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>
                                      ${agg.totalCostUsd.toFixed(4)} · {agg.avgLatencyMs.toFixed(0)} ms
                                    </Text>
                                    {agg.errorCount > 0 && (
                                      <Badge color="warning" size="small" appearance="outline">
                                        {agg.errorCount} err
                                      </Badge>
                                    )}
                                  </>
                                )}
                              </div>
                            ) : (
                              <Text size={100} style={{ color: tokens.colorNeutralForeground4 }}>—</Text>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ---- Ranking summary table ---- */}
          <div className={s.section}>
            <Title3 as="h2">Ranking</Title3>
            <table className={s.gridTable} style={{ marginTop: 12 }}>
              <thead>
                <tr>
                  <th className={s.thLeft}>#</th>
                  <th className={s.thLeft}>Prompt</th>
                  <th className={s.thLeft}>Model</th>
                  <th className={s.th} style={{ textAlign: 'right' }}>Overall</th>
                  <th className={s.th} style={{ textAlign: 'right' }}>Cost</th>
                  <th className={s.th} style={{ textAlign: 'right' }}>Latency</th>
                  <th className={s.th} style={{ textAlign: 'right' }}>Errors</th>
                </tr>
              </thead>
              <tbody>
                {sortedAggregates.map((agg, i) => {
                  const key = `${agg.promptId}|${agg.chatModelId}`;
                  const rank = rankByKey.get(key) ?? i + 1;
                  const allErrors = agg.errorCount === agg.runCount;
                  return (
                    <tr key={key}>
                      <td className={s.td}>
                        {allErrors ? '—' : (
                          <Badge
                            appearance="filled"
                            color={rank === 1 ? 'success' : rank === 2 ? 'warning' : 'informative'}
                          >
                            {rank}
                          </Badge>
                        )}
                      </td>
                      <td className={s.td}><Text weight="semibold">{agg.promptName}</Text></td>
                      <td className={s.td}>
                        <Badge color={providerBadgeColor(agg.providerId)} appearance="tint">
                          {agg.providerName} · {agg.modelName}
                        </Badge>
                      </td>
                      <td className={s.td} style={{ textAlign: 'right' }}>
                        {allErrors ? '—' : (
                          <Badge color={scoreColor(agg.avgOverallScore)}>
                            {pct(agg.avgOverallScore)}
                          </Badge>
                        )}
                      </td>
                      <td className={s.td} style={{ textAlign: 'right' }}>
                        ${agg.totalCostUsd.toFixed(4)}
                      </td>
                      <td className={s.td} style={{ textAlign: 'right' }}>
                        {agg.avgLatencyMs.toFixed(0)} ms
                      </td>
                      <td className={s.td} style={{ textAlign: 'right' }}>
                        {agg.errorCount > 0 ? (
                          <Badge color="danger" appearance="tint">{agg.errorCount}</Badge>
                        ) : (
                          <Badge color="success" appearance="tint">0</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ============================================================
          Drill-down dialog — per-question detail for one aggregate cell
          ============================================================ */}
      <Dialog
        open={drillCell !== null}
        onOpenChange={(_, d) => !d.open && setDrillCell(null)}
      >
        <DialogSurface style={{ maxWidth: 900, width: '90vw' }}>
          <DialogBody>
            <DialogTitle>
              {drillCell?.promptName} × {drillCell?.providerName} · {drillCell?.modelName}
            </DialogTitle>
            <DialogContent>
              {drillCell && (
                <>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    <Badge color={scoreColor(drillCell.avgOverallScore)} appearance="filled">
                      Avg {pct(drillCell.avgOverallScore)} overall
                    </Badge>
                    <Badge appearance="tint">
                      {drillCell.runCount - drillCell.errorCount} / {drillCell.runCount} succeeded
                    </Badge>
                    <Badge appearance="tint">${drillCell.totalCostUsd.toFixed(4)} total</Badge>
                    <Badge appearance="tint">{drillCell.avgLatencyMs.toFixed(0)} ms avg</Badge>
                  </div>
                  <Divider />
                  <div style={{ overflowX: 'auto', maxHeight: '55vh', overflowY: 'auto', marginTop: 8 }}>
                    <table className={s.drillTable}>
                      <thead>
                        <tr>
                          <th className={s.drillTh}>#</th>
                          <th className={s.drillTh}>Question</th>
                          <th className={s.drillTh}>Overall</th>
                          <th className={s.drillTh}>Matched keywords</th>
                          <th className={s.drillTh}>Answer (truncated)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {drillCells.map(cell => (
                          <tr key={cell.questionId}>
                            <td className={s.drillTd}>{cell.questionOrderIndex}</td>
                            <td className={s.drillTd}>
                              <Text size={200}>{cell.questionText}</Text>
                            </td>
                            <td className={s.drillTd}>
                              {cell.isError ? (
                                <Badge color="danger" appearance="tint" size="small">error</Badge>
                              ) : (
                                <Badge
                                  color={scoreColor(cell.overallScore)}
                                  appearance="tint"
                                  size="small"
                                >
                                  {pct(cell.overallScore)}
                                </Badge>
                              )}
                            </td>
                            <td className={s.drillTd}>
                              {cell.isError ? (
                                <Text size={100} style={{ color: tokens.colorPaletteRedForeground1 }}>
                                  {cell.error ?? 'Unknown error'}
                                </Text>
                              ) : (
                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                  {cell.matchedKeywords.length > 0
                                    ? cell.matchedKeywords.map(k => (
                                        <Badge key={k} appearance="outline" size="small">{k}</Badge>
                                      ))
                                    : <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>none</Text>
                                  }
                                </div>
                              )}
                            </td>
                            <td className={s.drillTd}>
                              <div className={s.answerSnippet} title={cell.answer}>
                                {cell.answer}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDrillCell(null)}>Close</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
